import { Button } from '@auctisync/ui';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { readClaims, useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

interface KeyRow {
  id: string;
  tenant_id: string;
  key_id: string;
  secret_preview: string;
  label: string | null;
  created_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
}

interface CreateResult {
  id: string;
  tenant_id: string;
  key_id: string;
  secret_hex: string;
  secret_preview: string;
  tenant_key_header: string;
}

/**
 * Admin UI for managing per-tenant HMAC keys (Module #11.8). Keys here
 * are consumed by external auctioneer software that signs bid requests
 * to the engine (Topic 6.4). The full secret is shown ONCE at creation
 * via a one-time modal; thereafter only the preview is visible.
 */
export function AdminAuctioneerKeys() {
  const { session } = useSession();
  const claims = readClaims(session);
  const tenantId = claims.tenantId;

  const [keys, setKeys] = useState<KeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [newSecret, setNewSecret] = useState<CreateResult | null>(null);

  const reload = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('auctioneer_hmac_keys')
      .select('id, tenant_id, key_id, secret_preview, label, created_at, revoked_at, last_used_at')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    if (err) {
      setError(err.message);
    } else {
      setKeys((data ?? []) as unknown as KeyRow[]);
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!tenantId) {
    return (
      <div>
        <h1 style={titleStyle}>Auctioneer keys</h1>
        <p style={{ color: 'var(--text-muted)' }}>
          You need a tenant role to manage auctioneer keys.
        </p>
      </div>
    );
  }

  return (
    <div>
      <header style={{ marginBottom: '1.5rem' }}>
        <h1 style={titleStyle}>Auctioneer keys</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>
          HMAC keys for external auctioneer software that places floor bids on behalf of phone /
          walk-in bidders. The engine verifies each signed request against the secret stored here.
          Secrets are shown <strong>only once</strong> at creation.
        </p>
      </header>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <Button onClick={() => setShowCreate(true)}>Generate new key</Button>
      </div>

      {error && (
        <div role="alert" style={alertStyle}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : keys.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>
          No keys yet. Generate one to give your auctioneer software something to sign with.
        </p>
      ) : (
        <KeyTable keys={keys} tenantId={tenantId} onChange={reload} />
      )}

      {showCreate && (
        <CreateKeyModal
          tenantId={tenantId}
          onClose={() => setShowCreate(false)}
          onCreated={(result) => {
            setShowCreate(false);
            setNewSecret(result);
            void reload();
          }}
        />
      )}

      {newSecret && <NewSecretModal result={newSecret} onClose={() => setNewSecret(null)} />}
    </div>
  );
}

function KeyTable({
  keys,
  tenantId,
  onChange,
}: {
  keys: KeyRow[];
  tenantId: string;
  onChange: () => Promise<void> | void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-card, 0.5rem)',
        overflow: 'hidden',
        background: 'var(--surface)',
      }}
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ background: 'var(--surface-elev)' }}>
              <Th>Key ID</Th>
              <Th>Label</Th>
              <Th>Secret preview</Th>
              <Th>Created</Th>
              <Th>Last used</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <KeyRowDisplay key={k.id} row={k} tenantId={tenantId} onChange={onChange} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KeyRowDisplay({
  row,
  tenantId,
  onChange,
}: {
  row: KeyRow;
  tenantId: string;
  onChange: () => Promise<void> | void;
}) {
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const header = `${tenantId}:${row.key_id}`;

  async function handleRevoke() {
    if (
      !confirm(
        `Revoke key "${row.key_id}"? Auctioneer software using this key will stop working immediately.`,
      )
    ) {
      return;
    }
    setRevoking(true);
    setError(null);
    const { error: err } = await supabase.rpc('revoke_auctioneer_hmac_key', {
      p_key_id: row.id,
    });
    setRevoking(false);
    if (err) {
      setError(err.message);
    } else {
      await onChange();
    }
  }

  return (
    <tr style={{ borderTop: '1px solid var(--admin-hairline, var(--border))' }}>
      <Td mono>{row.key_id}</Td>
      <Td>{row.label ?? '—'}</Td>
      <Td mono>{row.secret_preview}</Td>
      <Td>{new Date(row.created_at).toLocaleDateString()}</Td>
      <Td>{row.last_used_at ? new Date(row.last_used_at).toLocaleString() : 'Never'}</Td>
      <Td>
        {row.revoked_at ? (
          <span style={{ color: 'var(--text-danger, #991b1b)' }}>
            Revoked {new Date(row.revoked_at).toLocaleDateString()}
          </span>
        ) : (
          <span style={{ color: 'var(--text-success, #166534)' }}>Active</span>
        )}
      </Td>
      <Td>
        {!row.revoked_at && (
          <Button
            variant="outline"
            onClick={() => {
              void handleRevoke();
            }}
            disabled={revoking}
          >
            {revoking ? 'Revoking…' : 'Revoke'}
          </Button>
        )}
        {error && (
          <div style={{ color: 'var(--text-danger, #991b1b)', fontSize: '0.75rem' }}>{error}</div>
        )}
        {!row.revoked_at && (
          <div
            style={{
              marginTop: '0.25rem',
              fontSize: '0.7rem',
              color: 'var(--text-muted)',
              fontFamily: 'monospace',
            }}
            title="X-Tenant-Key header value"
          >
            X-Tenant-Key: {header}
          </div>
        )}
      </Td>
    </tr>
  );
}

function CreateKeyModal({
  tenantId,
  onClose,
  onCreated,
}: {
  tenantId: string;
  onClose: () => void;
  onCreated: (result: CreateResult) => void;
}) {
  const [keyId, setKeyId] = useState('');
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const trimmedLabel = label.trim();
    const args = {
      p_tenant_id: tenantId,
      p_key_id: keyId.trim(),
      ...(trimmedLabel ? { p_label: trimmedLabel } : {}),
    };
    const { data, error: err } = await supabase.rpc('create_auctioneer_hmac_key', args);
    setSubmitting(false);
    if (err) {
      setError(err.message);
    } else if (data) {
      onCreated(data as unknown as CreateResult);
    }
  }

  return (
    <ModalShell onClose={onClose} title="Generate new auctioneer key">
      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        style={{ display: 'grid', gap: '0.75rem' }}
      >
        <Field
          label="Key ID"
          hint="Pick a short identifier. 1–64 alphanumeric / underscore / dash characters (e.g., booth-1)."
        >
          <input
            type="text"
            value={keyId}
            onChange={(e) => setKeyId(e.target.value)}
            required
            pattern="^[A-Za-z0-9_-]{1,64}$"
            style={inputStyle}
            autoFocus
          />
        </Field>
        <Field label="Label (optional)" hint="Human-readable description for your records.">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            style={inputStyle}
          />
        </Field>
        {error && (
          <div role="alert" style={alertStyle}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Generating…' : 'Generate'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function NewSecretModal({ result, onClose }: { result: CreateResult; onClose: () => void }) {
  const [copied, setCopied] = useState<'header' | 'secret' | null>(null);

  async function copy(value: string, which: 'header' | 'secret') {
    await navigator.clipboard.writeText(value);
    setCopied(which);
    setTimeout(() => setCopied(null), 1500);
  }

  return (
    <ModalShell onClose={onClose} title="Copy your new key — shown only once">
      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
        Copy both values into your auctioneer software configuration <strong>now</strong>. The
        secret will not be retrievable after this dialog closes.
      </p>

      <Field label="X-Tenant-Key header">
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            type="text"
            value={result.tenant_key_header}
            readOnly
            style={{ ...inputStyle, fontFamily: 'monospace' }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void copy(result.tenant_key_header, 'header');
            }}
          >
            {copied === 'header' ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </Field>

      <Field label="HMAC secret (hex)">
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            type="text"
            value={result.secret_hex}
            readOnly
            style={{ ...inputStyle, fontFamily: 'monospace' }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void copy(result.secret_hex, 'secret');
            }}
          >
            {copied === 'secret' ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </Field>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
        <Button onClick={onClose}>I&rsquo;ve copied both values</Button>
      </div>
    </ModalShell>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--admin-hairline, var(--border))',
          borderRadius: 'var(--r-card, 0.5rem)',
          padding: '1.25rem',
          width: '100%',
          maxWidth: '32rem',
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          style={{
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--text)',
            marginBottom: '0.75rem',
          }}
        >
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'grid', gap: '0.25rem' }}>
      <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{hint}</span>}
    </label>
  );
}

function Th({ children }: { children: ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '0.5rem 0.75rem',
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'var(--text-muted)',
        fontWeight: 600,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, mono = false }: { children: ReactNode; mono?: boolean }) {
  return (
    <td
      style={{
        padding: '0.5rem 0.75rem',
        verticalAlign: 'top',
        fontFamily: mono ? 'monospace' : undefined,
      }}
    >
      {children}
    </td>
  );
}

const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-heading-active)',
  fontSize: '1.875rem',
  fontWeight: 700,
  color: 'var(--text)',
  marginBottom: '0.25rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
};

const alertStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  fontSize: '0.85rem',
};
